import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';
// public/src/components/user/master/PrintConditionManagement.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Modal, Badge } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import baseUrl from '../../../baseURL';

const PrintConditionManagement = () => {
  const [documentType, setDocumentType] = useState('pledge');
  const [printCondition, setPrintCondition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [newLine, setNewLine] = useState({ lineNumber: 1, englishText: '' });
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [isActive, setIsActive] = useState(true);

  const documentTypes = [
    { value: 'pledge', label: 'Pledge' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'release', label: 'Release' },
    { value: 'ledger', label: 'Ledger' },
    { value: 'pledgeBook', label: 'Pledge Book' },
    { value: 'auctionRegister', label: 'Auction Register' },
    { value: 'remainderLetter', label: 'Remainder Letter' },
    { value: 'preAuctionList', label: 'Pre Auction List' },
    { value: 'saleIntimation', label: 'Sale Intimation' },
    { value: 'dayBook', label: 'Day Book' },
    { value: 'journalRegister', label: 'Journal Register' },
    { value: 'profitLossAccount', label: 'Profit Loss Account' }
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'kn', name: 'Kannada' },
    { code: 'hi', name: 'Hindi' },
    { code: 'te', name: 'Telugu' },
    { code: 'ta', name: 'Tamil' },
    { code: 'mr', name: 'Marathi' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'or', name: 'Odia' },
    { code: 'ml', name: 'Malayalam' }
  ];

  useEffect(() => {
    fetchPrintCondition();
  }, [documentType]);

  const fetchPrintCondition = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/master/print-conditions?documentType=${documentType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setPrintCondition(data.data);
        setDefaultLanguage(data.data.defaultLanguage || 'en');
        setIsActive(data.data.isActive !== false);
      } else {
        setError(data.message || 'Failed to fetch print condition');
      }
    } catch (err) {
      setError('Error fetching print condition');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/master/print-conditions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentType,
          defaultLanguage,
          isActive
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Print condition saved successfully');
        setPrintCondition(data.data);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to save print condition');
      }
    } catch (err) {
      setError('Error saving print condition');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLine = () => {
    const maxLineNumber = printCondition?.textLines?.length > 0 
      ? Math.max(...printCondition.textLines.map(l => l.lineNumber)) + 1 
      : 1;
    setNewLine({ lineNumber: maxLineNumber, englishText: '' });
    setEditingLine(null);
    setShowAddLineModal(true);
  };

  const handleEditLine = (line) => {
    // Convert Mongoose Map to plain object if needed
    let translations = {};
    if (line.translations) {
      if (line.translations instanceof Map) {
        translations = Object.fromEntries(line.translations);
      } else if (typeof line.translations === 'object') {
        translations = line.translations;
      } else if (Array.isArray(line.translations)) {
        translations = Object.fromEntries(line.translations);
      }
    }
    
    setNewLine({
      lineNumber: line.lineNumber,
      englishText: line.englishText,
      translations: translations
    });
    setEditingLine(line);
    setShowAddLineModal(true);
  };

  const handleSaveLine = async () => {
    if (!newLine.englishText.trim()) {
      setError('English text is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/master/print-conditions/text-line`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          documentType,
          lineNumber: newLine.lineNumber,
          englishText: newLine.englishText,
          translations: newLine.translations || {}
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Text line saved successfully');
        setShowAddLineModal(false);
        fetchPrintCondition();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to save text line');
      }
    } catch (err) {
      setError('Error saving text line');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLine = async (lineNumber) => {
    if (!window.confirm(`Are you sure you want to delete line ${lineNumber}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/master/print-conditions/text-line/${lineNumber}?documentType=${documentType}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Text line deleted successfully');
        fetchPrintCondition();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to delete text line');
      }
    } catch (err) {
      setError('Error deleting text line');
    } finally {
      setLoading(false);
    }
  };

  const updateTranslation = (langCode, value) => {
    if (!newLine.translations) {
      setNewLine({ ...newLine, translations: {} });
    }
    setNewLine({
      ...newLine,
      translations: {
        ...newLine.translations,
        [langCode]: value
      }
    });
  };

  // Auto-translate English text to selected language
  const autoTranslate = async (langCode) => {
    if (!newLine.englishText || !newLine.englishText.trim() || langCode === 'en') {
      return;
    }

    // Check if translation already exists
    if (newLine.translations?.[langCode]) {
      return; // Don't overwrite existing translation
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}/translate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: newLine.englishText,
          targetLanguage: langCode
        })
      });

      const data = await response.json();
      if (data.success && data.data.translatedText) {
        updateTranslation(langCode, data.data.translatedText);
      }
    } catch (err) {
      // Don't show error to user, just log it
    }
  };

  // Auto-translate all languages when English text changes
  const handleEnglishTextChange = async (value) => {
    setNewLine({ ...newLine, englishText: value });
    
    // Auto-translate to all languages if English text is provided
    if (value && value.trim()) {
      // Translate to all non-English languages
      const nonEnglishLanguages = languages.filter(l => l.code !== 'en');
      for (const lang of nonEnglishLanguages) {
        // Only auto-translate if translation doesn't exist
        if (!newLine.translations?.[lang.code]) {
          await autoTranslate(lang.code);
        }
      }
    }
  };

  const sortedLines = printCondition?.textLines 
    ? [...printCondition.textLines].sort((a, b) => a.lineNumber - b.lineNumber)
    : [];

  return (
    <MasterPageShell title="Print Conditions" subtitle="Master data">
            <Container fluid className="mt-0 px-0">
        <Row>
          <Col>
            <Card className="border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Header style={{ background: 'transparent', borderBottom: '1px solid #e2e8f0' }}>
                <h5 className="mb-1 fw-bold">Configure print-back text lines</h5>
                <p className="text-muted mb-0 small">Conditions printed on the back of documents</p>
              </Card.Header>
              <Card.Body>
                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
                {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Document Type</Form.Label>
                      <Form.Select
                        value={documentType}
                        onChange={(e) => setDocumentType(e.target.value)}
                      >
                        {documentTypes.map(dt => (
                          <option key={dt.value} value={dt.value}>{dt.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Default Language</Form.Label>
                      <Form.Select
                        value={defaultLanguage}
                        onChange={(e) => setDefaultLanguage(e.target.value)}
                      >
                        {languages.map(lang => (
                          <option key={lang.code} value={lang.code}>{lang.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mt-4">
                      <Form.Check
                        type="switch"
                        id="isActiveSwitch"
                        label="Active"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mb-3">
                  <Button variant="primary" onClick={handleAddLine}>
                    <FaPlus className="me-2" />Add Text Line
                  </Button>
                  <Button variant="success" onClick={handleSave} disabled={loading}>
                    <FaSave className="me-2" />Save Settings
                  </Button>
                </div>

                {loading && !printCondition ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th style={{ width: '80px' }}>Line #</th>
                        <th>English Text</th>
                        <th>Translations</th>
                        <th style={{ width: '150px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLines.length > 0 ? (
                        sortedLines.map((line, index) => (
                          <tr key={index}>
                            <td className="text-center"><strong>{line.lineNumber}</strong></td>
                            <td>{line.englishText}</td>
                            <td>
                              {Object.keys(line.translations || {}).length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {Object.entries(line.translations || {}).map(([lang, text]) => (
                                    <Badge key={lang} bg="info" className="me-1">
                                      {languages.find(l => l.code === lang)?.name || lang}: {text}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted">No translations</span>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditLine(line)}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteLine(line.lineNumber)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            No text lines configured. Click "Add Text Line" to add conditions/terms for the back side.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Line Modal */}
      <Modal show={showAddLineModal} onHide={() => setShowAddLineModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingLine ? 'Edit Text Line' : 'Add Text Line'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Line Number</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={newLine.lineNumber}
                onChange={(e) => setNewLine({ ...newLine, lineNumber: parseInt(e.target.value) || 1 })}
                placeholder="Line number (order of display)"
                required
              />
              <Form.Text className="text-muted">
                Lower numbers appear first. Lines are sorted by line number.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>English Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newLine.englishText}
                onChange={(e) => handleEnglishTextChange(e.target.value)}
                placeholder="Enter the text/condition in English (this will be printed on the back side)"
                required
              />
              <Form.Text className="text-muted">
                This text will be printed on the back side of the document. Translations will be auto-generated when you enter text.
              </Form.Text>
            </Form.Group>

            <hr />
            <h5>Translations (Optional)</h5>
            <p className="text-muted">Add translations for other languages</p>

            {languages.filter(l => l.code !== 'en').map(lang => (
              <Form.Group key={lang.code} className="mb-3">
                <Form.Label>
                  {lang.name} ({lang.code})
                  {newLine.englishText && !newLine.translations?.[lang.code] && (
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="ms-2"
                      onClick={() => autoTranslate(lang.code)}
                      title="Auto-translate from English"
                    >
                      Auto Translate
                    </Button>
                  )}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={newLine.translations?.[lang.code] || ''}
                  onChange={(e) => updateTranslation(lang.code, e.target.value)}
                  placeholder={`Translation in ${lang.name} (auto-translated from English)`}
                />
                {newLine.translations?.[lang.code] && (
                  <Form.Text className="text-muted">
                    You can edit the auto-translated text if needed
                  </Form.Text>
                )}
              </Form.Group>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddLineModal(false)}>
            <FaTimes className="me-2" />Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveLine} disabled={loading}>
            <FaSave className="me-2" />Save Line
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterPageShell>
  );
};

export default PrintConditionManagement;
